import React, { useEffect, useMemo, useState } from 'react'
import { useBillingContext } from '~/contexts/BillingContext'
import { useModalContext } from '~/contexts/ModalContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useUser } from '~/hooks'
import { bridge } from '~/services/bridge'
import {
  getPersonalUsage,
  type BillableFlowDetailsParams,
  type PersonalUsageData
} from '~/services/usageDashboardService'
import { Typography } from '../common/Typography'
import { Modal } from '../modals/CurieModal'
import { CreditsCard } from './CreditsCard'
import { DialogHeader } from './DialogHeader'
import type { TimeRangeMode } from './DialogUsageDisplayContent'
import { UsageDisplayContent } from './DialogUsageDisplayContent'
import { downloadPersonalUsageAsExcel } from './downloadReport'
import { DownloadIcon } from './icons'
import { getIsBillableFlowPlan } from './types'
import { UsageOverviewCard } from './UsageOverviewCard'
import {
  resolveUsageApiRange,
  useCustomDateRangeFromLicense
} from './useCustomDateRangeFromLicense'

const BillableFlowsPanel = React.lazy(() =>
  import('./BillableFlowsTable').then(mod => ({ default: mod.BillableFlowsPanel }))
)

// Chat/prompt icon for conversation activity items
const ChatPromptIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    className={className}
  >
    <path
      d='M4.45227 14.4L7.7914 11.0608H12.8001C13.6838 11.0608 14.4001 10.3445 14.4001 9.46084V3.19998C14.4001 2.31632 13.6838 1.59998 12.8001 1.59998H3.2001C2.31644 1.59998 1.6001 2.31632 1.6001 3.19998V9.46084C1.6001 10.3445 2.31644 11.0608 3.2001 11.0608H4.45227V14.4Z'
      stroke='#8E8E8E'
      strokeWidth='1.3'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M4.6665 4.66663L6.6665 6.33329L4.6665 7.99996'
      stroke='#8E8E8E'
      strokeWidth='1.3'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <line
      x1='7.9835'
      y1='8.01663'
      x2='10.6835'
      y2='8.01663'
      stroke='#8E8E8E'
      strokeWidth='1.3'
      strokeLinecap='round'
    />
  </svg>
)

// Task icon for task activity items
const TaskIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    className={className}
  >
    <rect x='2' y='2' width='12' height='12' rx='2' stroke='#8E8E8E' strokeWidth='1.3' />
    <path
      d='M5 8L7 10L11 6'
      stroke='#8E8E8E'
      strokeWidth='1.3'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

interface PersonalUsageDialogProps {
  isOpen: boolean
  onClose: () => void
}

export const PersonalUsageDialog: React.FC<PersonalUsageDialogProps> = ({ isOpen, onClose }) => {
  const isPlugin = bridge.isInPlugin()
  const user = useUser()
  const { billingInfo, allLicenses, isBillingAdmin } = useBillingContext()
  const { currentWorkspace } = useWorkspaceContext()
  const { openManageAccount } = useModalContext()
  const [showAllActivities, setShowAllActivities] = useState(false)
  const [data, setData] = useState<PersonalUsageData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  // Filter state
  const [selectedWorkspaceUuid, setSelectedWorkspaceUuid] = useState<string | undefined>(
    currentWorkspace?.uuid
  )
  const [timeRangeMode, setTimeRangeMode] = useState<TimeRangeMode>('billing_period')

  const [showFlowsPanel, setShowFlowsPanel] = useState(false)

  const { customStartDate, customEndDate, onCustomStartDateChange, onCustomEndDateChange } =
    useCustomDateRangeFromLicense(
      billingInfo?.id,
      allLicenses?.period_start,
      allLicenses?.period_end
    )

  // Determine API parameters based on mode.
  // resolveUsageApiRange caps any future license_end at today so the chart doesn't
  // render empty future buckets (e.g. yearly Enterprise, sentinel 2099-12-31). The
  // trigger label is independent and still shows the contractual end date.
  const {
    timeRange: apiTimeRange,
    start: startDateParam,
    end: endDateParam
  } = useMemo(
    () =>
      resolveUsageApiRange({
        mode: timeRangeMode,
        customStart: customStartDate,
        customEnd: customEndDate,
        licenseStart: allLicenses?.period_start,
        licenseEnd: allLicenses?.period_end
      }),
    [
      timeRangeMode,
      customStartDate,
      customEndDate,
      allLicenses?.period_start,
      allLicenses?.period_end
    ]
  )

  // Fetch data when dialog opens or filters change
  useEffect(() => {
    if (!isOpen || !billingInfo?.id) return

    setIsLoading(true)
    getPersonalUsage(billingInfo.id, {
      timeRange: apiTimeRange,
      workspaceUuid: selectedWorkspaceUuid,
      startDate: startDateParam,
      endDate: endDateParam
    })
      .then(setData)
      .catch(console.error)
      .finally(() => setIsLoading(false))
  }, [isOpen, billingInfo?.id, apiTimeRange, selectedWorkspaceUuid, startDateParam, endDateParam])

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setShowAllActivities(false)
    }
  }, [isOpen])

  // Filter out conversations with 0 messages, but always show tasks
  const filteredActivities = (data?.recent_activities ?? []).filter(
    activity => activity.type === 'task' || activity.messages > 0
  )

  const displayedActivities = showAllActivities
    ? filteredActivities
    : filteredActivities.slice(0, 5)

  // Check if viewing all workspaces (for CreditsCard)
  const isAllWorkspacesSelected = selectedWorkspaceUuid === undefined

  const isBillableFlowPlan = getIsBillableFlowPlan(data?.credits, allLicenses)

  const flowDetailsParams: BillableFlowDetailsParams = useMemo(
    () => ({
      timeRange: apiTimeRange,
      workspaceUuid: selectedWorkspaceUuid,
      startDate: startDateParam,
      endDate: endDateParam,
      personalOnly: true
    }),
    [apiTimeRange, selectedWorkspaceUuid, startDateParam, endDateParam]
  )

  /**
   * Usage Overview + Card Stack for the personal usage view.
   *
   * Layout: [Usage Overview | Card Stack] (horizontal on desktop, vertical in plugin).
   * Card stack order, top → bottom:
   *   - Billable-flow plans: My Billable Flows, Team Billable Flows
   *   - Credit-based plans:  My Credit Usage, Team Credit Usage
   *
   * Sizing rule (desktop, mirrored in UsageStatsCards / CreditsCard):
   *   - Each card is fixed at ≈H/2 = 127px
   *   - Usage Overview has min-height H = 269px
   *   - 1 card  → stack = 127, Overview = 269, stack sits at top with room below
   *   - 2 cards → stack = 270 ≈ Overview (1px slack from gap-4)
   *   - 3 cards → stack = 397, Overview stretches via default flex stretch
   *   - 4 cards → stack = 524, Overview stretches further
   * Cards never shrink because their height is fixed. Guard tests below
   * lock in h-[127px] on each card and min-h-[269px] on the Overview.
   */
  const customCreditsSection = useMemo(() => {
    if (!data?.usage_overview || !data?.credits) return null

    const showCreditsCards = !isBillableFlowPlan

    return (
      // Outer row: Usage Overview on the left, card stack on the right.
      <div className={isPlugin ? 'flex flex-col gap-4' : 'flex gap-4'}>
        <UsageOverviewCard
          data={data.usage_overview}
          className={isPlugin ? 'w-full h-auto' : 'flex-1 min-h-[269px]'}
        />

        {/* Card stack (right column). 320px on desktop, full width in plugin. */}
        <div className={isPlugin ? 'flex flex-col gap-3 w-full' : 'flex flex-col gap-4 w-[320px]'}>
          {/* My Billable Flows — opens the flows detail panel on click. */}
          {isBillableFlowPlan && (
            <CreditsCard
              variant='billableFlowsOnly'
              data={data.credits}
              className='w-full'
              showLimits={timeRangeMode === 'billing_period'}
              isAllWorkspacesSelected={isAllWorkspacesSelected}
              isBillableFlowPlan={isBillableFlowPlan}
              billableFlowsTitle='My Billable Flows'
              onDetailsClick={() => setShowFlowsPanel(true)}
            />
          )}

          {/* Team Billable Flows — inline tile; billing admins get a Manage Account link. */}
          {isBillableFlowPlan && (
            <div
              className={`bg-tokens-neutral-fill-lighten-30 border border-tokens-neutral-bg-white rounded-2xl flex flex-col ${
                isPlugin ? 'px-4 py-4' : 'px-8 py-6 h-[127px]'
              }`}
            >
              <div className='flex items-start justify-between gap-3 mb-1'>
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='medium'
                  className='text-tokens-primary-1 select-none min-w-0'
                >
                  Team Billable Flows
                </Typography>
                {isBillingAdmin && (
                  <button
                    type='button'
                    onClick={() => {
                      onClose()
                      openManageAccount()
                    }}
                    className='cursor-pointer hover:opacity-70 transition-opacity shrink-0 self-start'
                  >
                    <Typography
                      fontFamily='geist'
                      fontSize='bodySm'
                      fontWeight='medium'
                      className='text-tokens-status-link-1 select-none whitespace-nowrap'
                    >
                      Manage Account
                    </Typography>
                  </button>
                )}
              </div>
              <div className='flex items-baseline'>
                <Typography
                  fontFamily='geist'
                  fontSize='titleLg'
                  fontWeight='bold'
                  className='text-tokens-primary-2 select-none'
                >
                  {(data.credits.team_billable_flows_used ?? 0).toLocaleString()}
                </Typography>
                {/* Denominator hidden in date-range mode since limits reset per cycle. */}
                {timeRangeMode === 'billing_period' && (
                  <Typography
                    fontFamily='geist'
                    fontSize='bodyMd'
                    fontWeight='normal'
                    className='text-tokens-neutral-text-tertiary ml-1 select-none'
                  >
                    /{' '}
                    {data.credits.billable_flows_limit != null &&
                    data.credits.billable_flows_limit > 0
                      ? data.credits.billable_flows_limit.toLocaleString()
                      : 'Unlimited'}
                  </Typography>
                )}
              </div>
            </div>
          )}

          {/* Credit-based plans: My Credit Usage (inline) + Team Credit Usage (CreditsCard). */}
          {showCreditsCards && (
            <>
              {/* My Credit Usage — personal credits consumed this period. */}
              <div
                className={`bg-tokens-neutral-fill-lighten-30 border border-tokens-neutral-bg-white rounded-2xl flex flex-col ${
                  isPlugin ? 'px-4 py-4' : 'px-8 py-6 h-[127px]'
                }`}
              >
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='medium'
                  className='text-tokens-primary-1 mb-1 select-none'
                >
                  My Credit Usage
                </Typography>
                <span data-testid='my-credit-usage-value'>
                  <Typography
                    fontFamily='geist'
                    fontSize='titleLg'
                    fontWeight='bold'
                    className='text-tokens-primary-2 select-none'
                  >
                    {(data.credits.personal_used ?? 0).toLocaleString()}
                  </Typography>
                </span>
              </div>

              {/* Team Credit Usage — limit + progress bar; Manage Account link for admins. */}
              <CreditsCard
                variant='creditsOnly'
                data={data.credits}
                className='w-full'
                showLimits={timeRangeMode === 'billing_period'}
                isAllWorkspacesSelected={isAllWorkspacesSelected}
                isBillableFlowPlan={isBillableFlowPlan}
                creditsSectionTitle='Team Credit Usage'
                showManageAccountLink={isBillingAdmin}
                onManageAccountClick={() => {
                  onClose()
                  openManageAccount()
                }}
              />
            </>
          )}
        </div>
      </div>
    )
  }, [
    data?.usage_overview,
    data?.credits,
    isAllWorkspacesSelected,
    isBillableFlowPlan,
    timeRangeMode,
    isBillingAdmin,
    isPlugin,
    onClose,
    openManageAccount
  ])

  const handleDownloadExcel = () => {
    if (!data || isDownloading) return
    setIsDownloading(true)
    try {
      downloadPersonalUsageAsExcel(data, 'personal-usage-report', {
        workspaceName: currentWorkspace?.name,
        billingEmail: billingInfo?.billing_email,
        reportName: 'Personal Usage Report',
        userName: data?.name ?? user?.name
      })
    } finally {
      setIsDownloading(false)
    }
  }

  const downloadButton = (
    <button
      onClick={handleDownloadExcel}
      className='h-[36px] flex items-center gap-[8px] px-[16px] py-[8px] rounded-[12px] cursor-pointer hover:bg-[#F5F5F5] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
      disabled={isDownloading || !data}
    >
      <DownloadIcon className='w-[16px] h-[16px] text-[#8E8E8E]' />
      <Typography
        fontFamily='geist'
        fontSize='bodyMd'
        fontWeight='medium'
        className='select-none'
        style={{ color: 'rgba(0, 0, 0, 0.65)', lineHeight: '22px', fontSize: '14px' }}
      >
        {isDownloading ? 'Downloading...' : 'Download report'}
      </Typography>
    </button>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={isPlugin ? 'w-full max-w-[calc(100vw-24px)]' : 'w-full max-w-[1058px]'}
      overlayClassName={isPlugin ? 'px-3' : 'px-[80px]'}
      hideCloseButton
      disableOutsideClick
      rawContent
    >
      <div className='flex flex-col bg-[#EFF0F3] h-[calc(100vh-120px)] overflow-hidden'>
        {/* Header - sticky at top */}
        <DialogHeader title='My Usage' onClose={onClose} className='shrink-0' />

        {/* Content - scrollable */}
        <div className={`flex-1 min-h-0 overflow-y-auto ${isPlugin ? 'px-3 py-4' : 'px-8 py-6'}`}>
          {/* User Info Row */}
          <div className='flex items-center mb-6'>
            <div className='flex flex-col gap-1'>
              <Typography
                fontFamily='geist'
                fontSize='titleMd'
                fontWeight='bold'
                className='select-none'
                style={{ color: 'rgba(0, 0, 0, 0.88)', fontSize: '18px', lineHeight: '24px' }}
              >
                {data?.name ?? user?.name ?? 'User'}
              </Typography>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='normal'
                className='select-none'
                style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '12px', lineHeight: '16px' }}
              >
                Last active {data?.last_active ?? 'Unknown'}
              </Typography>
            </div>
          </div>

          {/* Shared Usage Display Content */}
          <UsageDisplayContent
            usageOverview={data?.usage_overview}
            credits={data?.credits}
            selectedWorkspaceUuid={selectedWorkspaceUuid}
            onWorkspaceChange={setSelectedWorkspaceUuid}
            timeRangeMode={timeRangeMode}
            onTimeRangeModeChange={setTimeRangeMode}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onCustomStartDateChange={onCustomStartDateChange}
            onCustomEndDateChange={onCustomEndDateChange}
            showWorkspaceFilter={true}
            showTimeRangeSelector={true}
            layout='compact'
            customCreditsSection={customCreditsSection}
            headerActions={downloadButton}
            onCreditsDetailsClick={() => setShowFlowsPanel(true)}
          >
            {/* Billable Flows Panel (modal, opened via CreditsCard click) */}
            {billingInfo?.id && showFlowsPanel && (
              <React.Suspense fallback={null}>
                <BillableFlowsPanel
                  isOpen={showFlowsPanel}
                  onClose={() => setShowFlowsPanel(false)}
                  billingAccountId={billingInfo.id}
                  params={flowDetailsParams}
                  hideCredits={isBillableFlowPlan}
                />
              </React.Suspense>
            )}

            {/* Recent Activities Card */}
            {data && (
              <div
                className={`bg-[rgba(255,255,255,0.5)] border border-white rounded-[24px] ${isPlugin ? 'p-4' : 'p-6'}`}
              >
                {/* Header */}
                <div className='flex items-center justify-between mb-4'>
                  <Typography
                    fontFamily='geist'
                    fontSize='bodySm'
                    fontWeight='bold'
                    className='select-none'
                    style={{ color: '#2E1F36', fontSize: '14px', lineHeight: '18px' }}
                  >
                    Recent Activities
                  </Typography>
                  {filteredActivities.length > 5 && (
                    <button
                      onClick={() => setShowAllActivities(!showAllActivities)}
                      className='cursor-pointer hover:opacity-70 transition-opacity'
                    >
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        fontWeight='medium'
                        className='select-none'
                        style={{ color: '#846FE1', fontSize: '12px', lineHeight: '16px' }}
                      >
                        {showAllActivities ? 'Show less' : 'View all'}
                      </Typography>
                    </button>
                  )}
                </div>

                {/* Activity List */}
                <div
                  className={`flex flex-col ${showAllActivities ? 'max-h-[300px] overflow-y-auto' : ''}`}
                >
                  {displayedActivities.length === 0 ? (
                    <Typography
                      fontFamily='geist'
                      fontSize='bodySm'
                      fontWeight='normal'
                      className='select-none'
                      style={{ color: 'rgba(0, 0, 0, 0.45)' }}
                    >
                      No recent activities with messages
                    </Typography>
                  ) : (
                    displayedActivities.map(activity => (
                      <div
                        key={activity.id}
                        className='flex flex-col gap-1 px-2 py-1 rounded-[16px]'
                      >
                        <div className='flex items-center gap-3'>
                          {activity.type === 'task' ? (
                            <TaskIcon className='w-4 h-4 flex-shrink-0' />
                          ) : (
                            <ChatPromptIcon className='w-4 h-4 flex-shrink-0' />
                          )}
                          <Typography
                            fontFamily='geist'
                            fontSize='bodyMd'
                            fontWeight='medium'
                            className='flex-1 truncate'
                            style={{
                              color: 'rgba(0, 0, 0, 0.88)',
                              fontSize: '14px',
                              lineHeight: '22px'
                            }}
                          >
                            {activity.title}
                          </Typography>
                        </div>
                        <div className='pl-[28px]'>
                          <Typography
                            fontFamily='geist'
                            fontSize='bodySm'
                            fontWeight='normal'
                            className='select-none'
                            style={{
                              color: 'rgba(0, 0, 0, 0.45)',
                              fontSize: '11px',
                              lineHeight: 'normal'
                            }}
                          >
                            {activity.time}
                            {activity.type === 'task'
                              ? ' · Task'
                              : ` · ${activity.messages} messages`}
                          </Typography>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </UsageDisplayContent>
        </div>
      </div>
    </Modal>
  )
}

export default PersonalUsageDialog
